import { CasesService } from '@/@types/generated'
import { CaseComplainantType } from '@/@types/generated/models/CaseComplainantType'
import { createContext, useContext, useEffect, useState } from 'react'

interface CaseComplainantTypesContextProps {
  caseComplainantTypes: CaseComplainantType[]
}

const CaseComplainantTypesContext =
  createContext<CaseComplainantTypesContextProps>(undefined!)

export function useCaseComplainantTypes() {
  const context = useContext(CaseComplainantTypesContext)
  if (context === undefined) {
    throw new Error(
      'useCaseComplainantTypes must be used within a CaseComplainantTypesContext.Provider route'
    )
  }

  return context
}

export function CaseComplainantTypesProvider({
  children,
}: React.PropsWithChildren) {
  // State
  const [loading, setLoading] = useState(true)
  const [caseComplainantTypes, setCaseComplainantTypes] = useState<
    CaseComplainantType[]
  >([])

  // Load once
  useEffect(() => {
    setLoading(true)
    const load = async () => {
      try {
        setCaseComplainantTypes(await getComplainantTypes())
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  // No case yet, show loading
  if (loading) return <div>Retrieving case complainant types...</div>

  return (
    <CaseComplainantTypesContext.Provider value={{ caseComplainantTypes }}>
      {children}
    </CaseComplainantTypesContext.Provider>
  )
}

// Module-level cache and in-flight promise
const getComplainantTypes: () => Promise<CaseComplainantType[]> = (() => {
  let cache: CaseComplainantType[] | null = null
  let inFlight: Promise<CaseComplainantType[]> | null = null

  return async () => {
    if (cache) return cache
    if (inFlight) return inFlight

    // Load from API
    inFlight = CasesService.listCaseComplainantTypes().then(
      ({ caseComplainantTypes }) => {
        cache = caseComplainantTypes || []
        inFlight = null
        return cache
      }
    )

    return inFlight
  }
})()
